import React, { useState } from 'react';
import './Download.css';
import { FileText, Download as DownloadIcon, ChevronDown, ChevronUp } from 'lucide-react';

// Importing PDF score sheets from assets/scoresheets
import AmericanSkeet from '../../../../assets/scoresheets/AmericanSkeet.pdf';
import AmericanTrap from '../../../../assets/scoresheets/AmericanTrap.pdf';
import AmericanTrapDoubles from '../../../../assets/scoresheets/AmericanTrapDoubles.pdf';
import CompakSporting from '../../../../assets/scoresheets/CompakSporting.pdf';
import DoubleRise from '../../../../assets/scoresheets/DoubleRise.pdf';
import DownTheLine from '../../../../assets/scoresheets/DownTheLine.pdf';
import EnglishSkeet from '../../../../assets/scoresheets/EnglishSkeet.pdf';
import EnglishSporting from '../../../../assets/scoresheets/EnglishSporting.pdf';
import FITASCSporting from '../../../../assets/scoresheets/FITASCSporting.pdf';
import FiveStand from '../../../../assets/scoresheets/FiveStand.pdf';
import OlympicDoubleTrap from '../../../../assets/scoresheets/OlympicDoubleTrap.pdf';
import OlympicSkeetFinal from '../../../../assets/scoresheets/OlympicSkeetFinal.pdf';
import OlympicSkeetPre2012 from '../../../../assets/scoresheets/OlympicSkeet(Pre-2012).pdf';
import OlympicSkeet from '../../../../assets/scoresheets/OlympicSkeet.pdf';
import OlympicTrap from '../../../../assets/scoresheets/OlympicTrap.pdf';
import SingleBarrel from '../../../../assets/scoresheets/SingleBarrel.pdf';
import SkeetDoubles from '../../../../assets/scoresheets/SkeetDoubles.pdf';
import SkeetShootOff from '../../../../assets/scoresheets/SkeetShootOff.pdf';
import Sportrap from '../../../../assets/scoresheets/Sportrap.pdf';
import SuperSporting from '../../../../assets/scoresheets/SuperSporting.pdf';
import WobbleSkeet from '../../../../assets/scoresheets/WobbleSkeet.pdf';
import WobbleTrapABT from '../../../../assets/scoresheets/WobbleTrap(ABT).pdf';

const Download = () => {
    const [expandedSections, setExpandedSections] = useState({
        Skeet: true,
        Sporting: false,
        Trap: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const disciplineTypes = {
        "Skeet": [
            { name: "EnglishSkeet", file: EnglishSkeet, display: "English Skeet" },
            { name: "AmericanSkeet", file: AmericanSkeet, display: "American Skeet" },
            { name: "OlympicSkeet", file: OlympicSkeet, display: "Olympic Skeet" },
            { name: "OlympicSkeetPre2012", file: OlympicSkeetPre2012, display: "Olympic Skeet (Pre-2012)" },
            { name: "OlympicSkeetFinal", file: OlympicSkeetFinal, display: "Olympic Skeet Final" },
            { name: "SkeetDoubles", file: SkeetDoubles, display: "Skeet Doubles" },
            { name: "SkeetShootOff", file: SkeetShootOff, display: "Skeet Shoot Off" },
            { name: "WobbleSkeet", file: WobbleSkeet, display: "Wobble Skeet" }
        ],
        "Sporting": [
            { name: "CompakSporting", file: CompakSporting, display: "Compak Sporting" },
            { name: "EnglishSporting", file: EnglishSporting, display: "English Sporting" },
            { name: "FITASCSporting", file: FITASCSporting, display: "FITASC Sporting" },
            { name: "FiveStand", file: FiveStand, display: "Five Stand" },
            { name: "Sportrap", file: Sportrap, display: "Sportrap" },
            { name: "SuperSporting", file: SuperSporting, display: "Super Sporting" }
        ],
        "Trap": [
            { name: "AmericanTrap", file: AmericanTrap, display: "American Trap" },
            { name: "AmericanTrapDoubles", file: AmericanTrapDoubles, display: "American Trap Doubles" },
            { name: "DoubleRise", file: DoubleRise, display: "Double Rise" },
            { name: "DownTheLine", file: DownTheLine, display: "Down The Line" },
            { name: "OlympicDoubleTrap", file: OlympicDoubleTrap, display: "Olympic Double Trap" },
            { name: "OlympicTrap", file: OlympicTrap, display: "Olympic Trap" },
            { name: "SingleBarrel", file: SingleBarrel, display: "Single Barrel" },
            { name: "WobbleTrap", file: WobbleTrapABT, display: "Wobble Trap (ABT)" }
        ]
    };

    return (
        <div className="download-page">
            {/* Header */}
            <div className="download-header">
                <div className="download-header-content">
                    <h1>Score Sheets</h1>
                    <p>Download printable PDFs for the clay disciplines you shoot. Shoot Connect itself already works across mobile, tablet, and desktop.</p>
                </div>
            </div>

            {/* Score Sheets Section */}
            <div className="download-scores-section">
                <div className="download-scores-header">
                    <div className="download-scores-icon">
                        <FileText size={24} />
                    </div>
                    <div>
                        <h2>Score Sheets</h2>
                        <p>Keep these handy for printable scoring, venue use, coaching sessions, and offline backup on shoot days.</p>
                    </div>
                </div>

                <div className="download-scores-categories">
                    {Object.entries(disciplineTypes).map(([category, items]) => (
                        <div key={category} className="download-category">
                            <button 
                                className={`download-category-header ${expandedSections[category] ? 'expanded' : ''}`}
                                onClick={() => toggleSection(category)}
                            >
                                <span className="download-category-name">
                                    {category} Score Sheets
                                    <span className="download-category-count">{items.length}</span>
                                </span>
                                {expandedSections[category] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </button>
                            
                            {expandedSections[category] && (
                                <div className="download-category-items">
                                    {items.map((item) => (
                                        <a 
                                            key={item.name} 
                                            href={item.file} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="download-item"
                                        >
                                            <div className="download-item-icon">
                                                <FileText size={18} />
                                            </div>
                                            <span className="download-item-name">{item.display}</span>
                                            <div className="download-item-action">
                                                <DownloadIcon size={16} />
                                                PDF
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Download;
